import { RoutePath } from '@quizzach/interfaces';
import { Colors, H3 } from '@quizzach/styles';
import { QuizIntroContainer } from '@quizzach/play-styles';
import { useNavigate, useParams } from 'react-router-dom';
import { useTvCode } from '@quizzach/api-hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	FitToViewport,
	InlineAlertMessage,
	TvAppInfoModal,
} from '@quizzach/components';
import { IcArrowRight, QLogo } from '@quizzach/assets';
import { Reveal } from '@quizzach/motion';
import { useWindowListener } from '@quizzach/contexts';

const REFRESH_INTERVAL_MS = 30 * 1000; // half a minute

export const QuizPlay = () => {
	const { t } = useTranslation();
	const { uuid } = useParams<{ uuid: string }>();
	const navigate = useNavigate();
	const { code, expiresAt, loading, error, getCode } = useTvCode();
	const [showTvInfo, setShowTvInfo] = useState(false);
	const { touchDevice } = useWindowListener();

	const expiresAtSec = (() => {
		if (expiresAt === null) return null;
		const numericExpires = Number(expiresAt);
		return Number.isFinite(numericExpires) ? numericExpires : null;
	})();

	const isExpired =
		expiresAtSec !== null && expiresAtSec <= Math.floor(Date.now() / 1000);
	const displayedCode = (!isExpired ? code : '').padEnd(4, ' ');
	const showDigits = !loading && Boolean(code);

	// Rendered twice (CSS shows one per breakpoint): on mobile it shares the
	// caption line with the code, on desktop it sits below the description.
	const moreButton = (className: string) => (
		<button
			type='button'
			className={`quiz-play-more ${className}`}
			onClick={() => setShowTvInfo(true)}
		>
			<span className='q' aria-hidden='true'>
				<QLogo size={11} />
			</span>
			{t('quiz.tv.moreLink')}
		</button>
	);

	useEffect(() => {
		if (!uuid) return;
		getCode(uuid);
	}, [uuid, getCode]);

	useEffect(() => {
		if (!uuid) return;
		const intervalId = window.setInterval(() => {
			getCode(uuid);
		}, REFRESH_INTERVAL_MS);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [uuid, getCode]);

	useEffect(() => {
		if (!uuid || expiresAtSec === null) return;

		const now = Date.now();
		const expiresMs = expiresAtSec * 1000;
		if (expiresMs <= now) {
			getCode(uuid);
			return;
		}

		const timeoutId = window.setTimeout(() => {
			getCode(uuid);
		}, expiresMs - now);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [expiresAtSec, uuid, getCode]);

	const content = (
		<>
			<Reveal className='quiz-mode-titlewrap'>
				<H3 className='quiz-mode-title' color={Colors.textMain}>
					{t('quiz.tv.title')}
				</H3>
			</Reveal>
			<div className='quiz-mode-rows quiz-play-rows'>
				{/* The TV row carries the code and the whole story in its
				    description; it is informational, so no wash and no arrow —
				    only the device row is an action. */}
				<Reveal className='quiz-mode-row static'>
					<div className='in'>
						<span className='num'>01</span>
						<div className='main'>
							<span className='quiz-play-new'>{t('quiz.tv.fresh')}</span>
							<h3>{t('quiz.tv.tvRow')}</h3>
							{error && (
								<InlineAlertMessage
									type='error'
									message={t('quiz.tv.codeError')}
									style={{ marginTop: 8 }}
								/>
							)}
						</div>
						<div className='quiz-play-code-block'>
							{moreButton('mob')}
							<div className='quiz-play-code-col'>
								<div className={`quiz-play-code ${showDigits ? 'show' : ''}`}>
									{showDigits && (
										<>
											<span>{displayedCode[0]}</span>
											<span>{displayedCode[1]}</span>
											<span>{displayedCode[2]}</span>
											<span>{displayedCode[3]}</span>
										</>
									)}
									{loading && !code && (
										<div
											className='quiz-play-code-dots'
											aria-label={t('quiz.tv.loadingCode')}
										/>
									)}
								</div>
								<span className='quiz-play-code-label'>
									{t('quiz.tv.codeLabel')}
								</span>
							</div>
						</div>
						<span className='desc'>
							{t('quiz.tv.tvRowDescription')}
							{moreButton('desk')}
						</span>
					</div>
				</Reveal>

				<Reveal
					className='quiz-mode-row'
					onClick={() =>
						navigate(`/quiz/${uuid}/${RoutePath.QuizModeSelection}`)
					}
				>
					<div className='in'>
						<span className='num'>02</span>
						<div className='main'>
							<h3>{t('quiz.tv.deviceRow')}</h3>
						</div>
						<span className='desc'>{t('quiz.tv.deviceRowDescription')}</span>
						<span className='arr'>
							<IcArrowRight size={20} />
						</span>
					</div>
				</Reveal>
			</div>
		</>
	);

	return (
		<QuizIntroContainer
			data-id='quiz-play-container-00'
			$noBottomBar={touchDevice}
		>
			<TvAppInfoModal
				show={showTvInfo}
				closeModal={() => setShowTvInfo(false)}
			/>
			{/* Mobile scrolls naturally; the fit-to-viewport scaling is a
			    desktop/presentation concern. The spacers seat the rows in the
			    lower third of the stage, same as the start screen. */}
			{touchDevice ? (
				<>
					<span className='quiz-start-space' aria-hidden='true' />
					{content}
					<span className='quiz-start-space bottom' aria-hidden='true' />
				</>
			) : (
				<FitToViewport topAligned>{content}</FitToViewport>
			)}
		</QuizIntroContainer>
	);
};
